import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/vue-query'
import { getAssetSettings, getAssetSettingsLookups, getAssetCustomers} from '@/api/assets/get-asset-settings'
import { toValue, type MaybeRefOrGetter } from 'vue'
import type { UpdateAssetSettings, UpdateManualAsset  } from '@/api/assets/schemas'
import { saveAssetSettings } from '@/api/assets/save-asset-settings'
import { createLogger } from "@/helpers/logging";
import { getManualAssetDetails, updateManualAssetDetails} from '@/api/assets/assets'
import { updateMeterConfiguration } from '@/api/assets/update-meter-display-config'
import { attachReconFile } from '@/features/assets/api/attachAssetSettingsFile'
import { useMutationWithInvalidation } from '@/shared/composables/useMutationWithInvalidation'
import { removeYieldFile } from '@/features/assets/api/removeYieldFile'
import { removeReconFile } from '@/features/assets/api/removeAssetSettingsFile'
import { AssetDeliveryStatus, AssetType } from '@/features/assets/constants'
import { fetchPaginatedAssets } from '@/features/assets/api/fetchPaginatedAssets'
import type { DistributiveOmit } from '@/shared'

export function useAssetSettings(
  assetCode: MaybeRefOrGetter<string | undefined>
) {
  return useQuery({
    queryKey: ['assets:settings', assetCode],
    meta: {
      logger: createLogger(`composables/assets/useAssetSettings`)
    },
    queryFn: () =>
      getAssetSettings(toValue(assetCode)!),
    enabled() {
      const value = toValue(assetCode)

      return value !== undefined
    }
  })
}

export function useWheelingAssets(){
  return useInfiniteQuery({
    queryKey: ['assets', 'wheeling', 'operational'],
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) =>
      fetchPaginatedAssets({
        page: pageParam,
        pageSize: 30,
        filter: AssetType.Wheeling,
        status: AssetDeliveryStatus.Operational
      }),
    getNextPageParam: (lastPage: any) => {
      const { page , pageSize, totalRecords } = lastPage.meta

      const totalPages = Math.ceil(totalRecords / pageSize)

      if( page > totalPages ) return undefined

      return page + 1
    }
  })
}

export function useAssetSettingsLookups() {
  return useQuery({
    queryKey: ['assets:settingsLookups'],
    meta: {
      logger: createLogger(`composables/assets/useAssetSettingsLookups`)
    },
    queryFn: () =>
      getAssetSettingsLookups()
  })
}

export function useAssetCustomers(contractFundEntityId: MaybeRefOrGetter<number|undefined>) {
  return useQuery({
    queryKey: ['assets:customers', contractFundEntityId],
    meta: {
      logger: createLogger(`composables/assets/useAssetCustomers`)
    },
    enabled() {
      const v = toValue(contractFundEntityId)
      return v !== undefined && v > 0
    },
    queryFn: () =>
      getAssetCustomers(toValue(contractFundEntityId)!)
  })
}

export function useSaveAssetSettings(assetCode: MaybeRefOrGetter<string | undefined>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: MaybeRefOrGetter<DistributiveOmit<UpdateAssetSettings, 'contacts'|'tariffProfile'>>) => saveAssetSettings(toValue(assetCode)!, toValue(settings)),
    onSuccess: async () => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
        queryClient.invalidateQueries({ queryKey: ['assets:details', assetCode] }),
        queryClient.invalidateQueries({ queryKey: ['assets:settings', assetCode] }),
        queryClient.invalidateQueries({ queryKey: ['invoices:config', assetCode] }),
      ])
    },
  })
}

type MeterConfig = {
  id: number,
  hide: boolean
}

export function useSaveMeterConfiguration(assetCode: MaybeRefOrGetter<string>){
  return useMutation({
    mutationFn: (meterConfigs: MeterConfig []) => updateMeterConfiguration(toValue(assetCode), meterConfigs)
  })
}

export function useGetManualAssetDetails(assetCode: MaybeRefOrGetter<string>) {
  return useQuery({
    meta: {
      logger: createLogger('composables/assets/useGetManualAssetDetails')
    },
    queryKey: ['assets:details:manual', assetCode],
    queryFn: () => getManualAssetDetails(toValue(assetCode))
  })
}

export function useUpdateManualAsset(assetCode: MaybeRefOrGetter<string>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (manualAssetDetails: UpdateManualAsset) => updateManualAssetDetails(toValue(assetCode), manualAssetDetails),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['assets:details:manual', assetCode] });
      await queryClient.invalidateQueries({ queryKey: ['assets:settings', assetCode] });
    }
  });
}

export function useReconFileRemoval(assetCode: MaybeRefOrGetter<string>) {
  const queryClient = useQueryClient();
   return useMutation({
    mutationFn: () => removeReconFile(toValue(assetCode)),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['assets:details:manual', assetCode] });
      await queryClient.invalidateQueries({ queryKey: ['assets:settings', assetCode]});
    }
  });
}

export const useReconFileAttachment = (assetCode: MaybeRefOrGetter<string>) => useMutationWithInvalidation(
  (uploadId: string) => attachReconFile(toValue(assetCode), uploadId),
  [
    ['assets:details:manual', assetCode],
    ['assets:settings', assetCode],
  ]
)
